import { useState, useEffect, useCallback } from 'react';
import { useCrudErrorHandler } from './useErrorHandler';
import { AppError } from '../../types/error';

interface CrudOperations<T> {
  getAll: () => Promise<T[]>;
  create: (item: Omit<T, 'id'>) => Promise<T>;
  update: (item: T) => Promise<T>;
  delete: (id: string | number) => Promise<void>;
}

interface CrudState<T> {
  items: T[];
  loading: boolean;
  error: AppError | null;
}

interface CrudActions<T> {
  handleCreate: (item: Omit<T, 'id'>) => Promise<void>;
  handleUpdate: (item: T) => Promise<void>;
  handleDelete: (id: string | number) => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

export const useCrud = <T extends { id: string | number }>(
  operations: CrudOperations<T>,
  entityName: string = 'item'
): CrudState<T> & CrudActions<T> => {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const { error, clearError, handleCrudOperation } = useCrudErrorHandler();

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await handleCrudOperation(() => operations.getAll(), 'fetch', entityName);
    if (result) {
      setItems(result);
    }
    setLoading(false);
  }, [operations, entityName, handleCrudOperation]);

  const handleCreate = useCallback(
    async (item: Omit<T, 'id'>) => {
      const result = await handleCrudOperation(() => operations.create(item), 'create', entityName);
      if (result) {
        setItems(prev => [...prev, result]);
      } else {
        throw new Error('Create operation failed');
      }
    },
    [operations, entityName, handleCrudOperation]
  );

  const handleUpdate = useCallback(
    async (item: T) => {
      const result = await handleCrudOperation(() => operations.update(item), 'update', entityName);
      if (result) {
        setItems(prev => prev.map(i => (i.id === item.id ? result : i)));
      } else {
        throw new Error('Update operation failed');
      }
    },
    [operations, entityName, handleCrudOperation]
  );

  const handleDelete = useCallback(
    async (id: string | number) => {
      const result = await handleCrudOperation(() => operations.delete(id), 'delete', entityName);
      if (result !== null) {
        setItems(prev => prev.filter(i => i.id !== id));
      } else {
        throw new Error('Delete operation failed');
      }
    },
    [operations, entityName, handleCrudOperation]
  );

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    items,
    loading,
    error,
    handleCreate,
    handleUpdate,
    handleDelete,
    refresh,
    clearError,
  };
};
